import {
  CONTRACT_TEMPLATE_DOCX_CONTENT_TYPE,
  CONTRACT_TEMPLATE_MAX_FILE_SIZE_BYTES,
} from '@clickersphere/core/services/ContractTemplateService';
import { Button } from '../../Button';
import ConfirmationModal from '../../ConfirmationModal';
import FileDropZone from '../../FileDropZone';
import { Input } from '../../Input';
import Loader from '../../Loader';
import SegmentedControl from '../../SegmentedControl';
import Switch from '../../Switch';
import AnalysisStatusBanner from './AnalysisStatusBanner';
import DocumentVariableEditor from './DocumentVariableEditor';
import VariableMappingEditor from './VariableMappingEditor';
import {
  EditorMobileTab,
  useTemplateModalController,
} from './useTemplateModalController';

export default function TemplateModal() {
  const {
    t,
    isEditing,
    step,
    name,
    nameError,
    active,
    selectedFileName,
    fileError,
    analysisStatus,
    analysisReason,
    analysisVariableCount,
    canSuggestVariables,
    showEmptyDocumentWarning,
    hasSelectableText,
    docxFileKey,
    paragraphs,
    isParagraphsLoading,
    variables,
    variableErrors,
    confirmMissingAnchors,
    mobileTab,
    uploadProgress,
    isUploading,
    isAnalyzing,
    isExtractingParagraphs,
    isProcessingFile,
    isSaving,
    handleNameChange,
    handleActiveChange,
    handleFileSelected,
    handleSuggestVariables,
    handleContinue,
    handleBack,
    handleVariableChange,
    handleVariableRemove,
    handleVariableAdd,
    handleCreateAnchor,
    handleRemoveAnchor,
    handleRemoveAnchorByMatch,
    handleLocateVariable,
    handleSubmit,
    handleConfirmMissingAnchors,
    handleCancelMissingAnchors,
    handleClose,
    handleMobileTabChange,
  } = useTemplateModalController();

  const mobileTabOptions: { value: EditorMobileTab; label: string }[] = [
    { value: 'document', label: t('contract.templates.editor.documentTab') },
    { value: 'variables', label: t('contract.templates.editor.variablesTab') },
  ];

  return (
    <div className="my-4 flex max-h-[70vh] w-full flex-col gap-4 overflow-y-auto px-1 md:max-h-[78vh]">
      {step === 'upload' && (
        <>
          <Input
            type="text"
            name="templateName"
            placeholder={t('contract.templates.modal.nameField')}
            value={name}
            error={nameError}
            disabled={isProcessingFile}
            onChange={(event) => handleNameChange(event.target.value)}
          />

          <section className="flex flex-col gap-2">
            <span className="text-text-secondary text-sm font-medium">
              {t('contract.templates.modal.upload.label')}
            </span>

            <FileDropZone
              acceptedTypes={[CONTRACT_TEMPLATE_DOCX_CONTENT_TYPE]}
              acceptedExtensions={['.docx']}
              maxSizeBytes={CONTRACT_TEMPLATE_MAX_FILE_SIZE_BYTES}
              fileName={selectedFileName}
              placeholder={
                selectedFileName
                  ? t('contract.templates.modal.upload.changeFile')
                  : t('contract.templates.modal.upload.selectFile')
              }
              hint={t('contract.templates.modal.upload.hint')}
              inputLabel={t('contract.templates.modal.upload.label')}
              isUploading={isUploading}
              progress={uploadProgress}
              error={fileError}
              disabled={isProcessingFile}
              onFileSelected={handleFileSelected}
            />

            {isExtractingParagraphs && (
              <div className="flex flex-col items-center gap-2 py-4 text-center">
                <Loader className="h-8 w-8" />
                <strong className="text-text-primary text-sm">
                  {t('contract.templates.modal.upload.reading')}
                </strong>
              </div>
            )}

            {showEmptyDocumentWarning && (
              <section
                role="status"
                className="flex flex-col gap-1 rounded-lg bg-yellow-50 p-3 text-sm text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200"
              >
                <strong className="font-semibold">
                  {t('contract.templates.modal.upload.analysis.emptyTitle')}
                </strong>
                <p>
                  {t(
                    'contract.templates.modal.upload.analysis.reason.DOCUMENT_EMPTY',
                  )}
                </p>
              </section>
            )}
          </section>

          <section className="mt-2 flex w-full flex-col gap-3 sm:flex-row sm:gap-4">
            <Button
              className="w-full sm:w-50"
              labelPath="contract.templates.modal.cancel"
              color="ghost"
              onClick={handleClose}
            />
            <Button
              className="w-full"
              labelPath="contract.templates.modal.next"
              disabled={isProcessingFile || !docxFileKey || !hasSelectableText}
              onClick={handleContinue}
            />
          </section>
        </>
      )}

      {step === 'variables' && (
        <>
          {isEditing && (
            <>
              <Input
                type="text"
                name="templateName"
                placeholder={t('contract.templates.modal.nameField')}
                value={name}
                error={nameError}
                disabled={isSaving}
                onChange={(event) => handleNameChange(event.target.value)}
              />
              <Switch
                id="templateActive"
                checked={active}
                onCheckedChange={handleActiveChange}
                disabled={isSaving}
                label={t('contract.templates.modal.activeField')}
              />
            </>
          )}

          <AnalysisStatusBanner
            status={analysisStatus}
            reason={analysisReason}
            variableCount={analysisVariableCount}
          />

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-text-primary text-sm font-semibold">
              {t('contract.templates.editor.title')}
            </h3>

            {canSuggestVariables && (
              <Button
                color="ghost"
                labelPath="contract.templates.modal.upload.analysis.suggest"
                className="min-h-10 w-full px-4 sm:w-auto"
                disabled={isSaving}
                isLoading={isAnalyzing}
                onClick={handleSuggestVariables}
              />
            )}
          </div>

          <SegmentedControl
            className="md:hidden"
            aria-label={t('contract.templates.editor.title')}
            value={mobileTab}
            options={mobileTabOptions}
            onChange={handleMobileTabChange}
          />

          <div className="flex flex-col gap-4 md:grid md:grid-cols-2 md:items-start">
            <div
              className={
                mobileTab === 'document'
                  ? 'flex flex-col'
                  : 'hidden md:flex md:flex-col'
              }
            >
              <DocumentVariableEditor
                paragraphs={paragraphs}
                variables={variables}
                isLoading={isParagraphsLoading}
                onCreateAnchor={handleCreateAnchor}
                onRemoveAnchorByMatch={handleRemoveAnchorByMatch}
              />
            </div>

            <div
              className={
                mobileTab === 'variables'
                  ? 'flex flex-col'
                  : 'hidden md:flex md:flex-col'
              }
            >
              <VariableMappingEditor
                variables={variables}
                errors={variableErrors}
                disabled={isSaving}
                onChange={handleVariableChange}
                onRemove={handleVariableRemove}
                onRemoveAnchor={handleRemoveAnchor}
                onLocateVariable={handleLocateVariable}
                onAdd={handleVariableAdd}
              />
            </div>
          </div>

          <section className="mt-2 flex w-full flex-col gap-3 sm:flex-row sm:gap-4">
            {!isEditing && (
              <Button
                className="w-full sm:w-50"
                labelPath="contract.templates.modal.back"
                color="ghost"
                disabled={isSaving}
                onClick={handleBack}
              />
            )}
            {isEditing && (
              <Button
                className="w-full sm:w-50"
                labelPath="contract.templates.modal.cancel"
                color="ghost"
                disabled={isSaving}
                onClick={handleClose}
              />
            )}
            <Button
              className="w-full"
              labelPath="contract.templates.modal.save"
              disabled={isSaving}
              isLoading={isSaving}
              onClick={handleSubmit}
            />
          </section>
        </>
      )}

      {confirmMissingAnchors && (
        <ConfirmationModal
          title={t('contract.templates.confirmMissingAnchors.title')}
          text={t('contract.templates.confirmMissingAnchors.text')}
          secondaryText={t(
            'contract.templates.confirmMissingAnchors.secondaryText',
          )}
          confirmButtonText={t(
            'contract.templates.confirmMissingAnchors.confirm',
          )}
          cancelButtonText={t(
            'contract.templates.confirmMissingAnchors.cancel',
          )}
          isLoading={isSaving}
          onConfirm={handleConfirmMissingAnchors}
          onCancel={handleCancelMissingAnchors}
          onClose={handleCancelMissingAnchors}
        />
      )}
    </div>
  );
}
